import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
  Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { CostingService } from '../../services/costing.service';
import { AuthService } from '../../services/auth.service';
import { OrderCostRow, PendingShipment } from '../../interfaces/costing.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const OrderCosts: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.COSTING_MANAGE);
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState<OrderCostRow[]>([]);
  const [pending, setPending] = useState<PendingShipment[]>([]);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [orders, ship] = await Promise.all([
        CostingService.listOrders(institutionId, { status: status || undefined, size: 100 }),
        CostingService.pendingShipments(institutionId),
      ]);
      setRows(orders.content ?? []);
      setPending(ship ?? []);
    } catch (err) {
      toast.showApiError(err, 'Could not load order costs');
    }
  }, [institutionId, status, toast]);

  useEffect(() => { load(); }, [load]);

  const [dialog, setDialog] = useState<PendingShipment | null>(null);
  const [costDate, setCostDate] = useState('');

  const openDialog = (p: PendingShipment) => {
    setDialog(p);
    setCostDate(p.suggestedDate ?? p.invoiceDate);
  };

  const costShipment = async (p: PendingShipment, date: string) => {
    setBusy(p.exportInvoiceId);
    try {
      const ev = await CostingService.costShipment(institutionId, p.exportInvoiceId, { date: date || undefined });
      if (ev?.dateNote) toast.showWarning(`Shipment ${p.invoiceNumber} costed. ${ev.dateNote}`);
      else toast.showSuccess(`Shipment ${p.invoiceNumber} costed`);
      setDialog(null);
      await load();
    } catch (err) {
      toast.showApiError(err, 'Could not cost the shipment');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ p: 2 }} data-help-page="costing.orders.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }} data-help-section="costing.orders.filters">
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Order costs</Typography>
        <TextField data-help="costing.orders.status" sx={{ flex: '1 1 160px', maxWidth: 220 }} size="small" label="Order status" value={status}
          onChange={e => setStatus(e.target.value.toUpperCase())} placeholder="e.g. SHIPPED" />
        <Button data-help="costing.orders.output" component={RouterLink} to="/costing/output">Production output</Button>
        <Button data-help="costing.orders.absorption" component={RouterLink} to="/costing/absorption">Absorption runs</Button>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab data-help="costing.orders.tab.orders" label="Orders" />
        <Tab data-help="costing.orders.tab.pending" label={`Shipments to cost (${pending.length})`} />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper} data-help-section="costing.orders.table">
          <Table size="small" data-testid="order-cost-table">
            <TableHead><TableRow>
              <TableCell data-help="costing.orders.col.order">Order</TableCell><TableCell data-help="costing.orders.col.status">Status</TableCell><TableCell align="right" data-help="costing.orders.col.revenue">Revenue</TableCell>
              <TableCell align="right" data-help="costing.orders.col.cogs">COGS</TableCell><TableCell align="right" data-help="costing.orders.col.expense">Tagged expense</TableCell>
              <TableCell align="right" data-help="costing.orders.col.wip">WIP</TableCell><TableCell align="right" data-help="costing.orders.col.fg">FG</TableCell>
              <TableCell align="right" data-help="costing.orders.col.margin">Margin</TableCell><TableCell align="right" data-help="costing.orders.col.marginPct">Margin %</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.orderId} hover>
                  <TableCell><RouterLink to={`/costing/orders/${r.orderId}`}>{r.orderNumber}</RouterLink></TableCell>
                  <TableCell>{humanizeEnum(r.status)}{r.closedWithResidual && <Chip size="small" color="warning" sx={{ ml: 1 }} label="Residual" />}</TableCell>
                  <TableCell align="right">{formatMoney(r.revenue)}</TableCell>
                  <TableCell align="right">{formatMoney(r.cogs)}</TableCell>
                  <TableCell align="right">{formatMoney(r.taggedExpense)}</TableCell>
                  <TableCell align="right">{formatMoney(r.wipBalance)}</TableCell>
                  <TableCell align="right">{formatMoney(r.fgBalance)}</TableCell>
                  <TableCell align="right">{formatMoney(r.margin)}</TableCell>
                  <TableCell align="right">{r.costIncomplete ? <Chip size="small" color="warning" variant="outlined" label="Cost incomplete" /> : (r.marginPct == null ? '-' : `${r.marginPct}%`)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={9}>No orders.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <>
          <Alert severity="info" sx={{ mb: 1 }}>Costing a shipment moves the finished goods of the invoiced pieces to COGS. Transfer WIP to FG on the order first.</Alert>
          <TableContainer component={Paper} data-help-section="costing.orders.pending">
            <Table size="small" data-testid="pending-shipments-table">
              <TableHead><TableRow>
                <TableCell data-help="costing.orders.pending.col.invoice">Invoice</TableCell><TableCell data-help="costing.orders.pending.col.date">Date</TableCell><TableCell data-help="costing.orders.pending.col.order">Order</TableCell>
                <TableCell align="right" data-help="costing.orders.pending.col.qty">Qty</TableCell><TableCell align="right" data-help="costing.orders.pending.col.fgOnHand">FG on hand</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {pending.map(p => (
                  <TableRow key={p.exportInvoiceId}>
                    <TableCell>{p.invoiceNumber}</TableCell><TableCell>{p.invoiceDate}</TableCell><TableCell>{p.orderNumber}</TableCell>
                    <TableCell align="right">{p.quantity ?? '-'}</TableCell><TableCell align="right">{p.fgQuantityOnHand}</TableCell>
                    <TableCell align="right">
                      {canManage && <Button data-help="costing.orders.pending.cost" size="small" variant="outlined" disabled={busy === p.exportInvoiceId} onClick={() => openDialog(p)}>Cost shipment</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {pending.length === 0 && <TableRow><TableCell colSpan={6}>Every invoiced shipment is costed.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Dialog data-help-page="costing.orders.dlg" open={!!dialog} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Cost shipment {dialog?.invoiceNumber}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info">
            The cost of goods sold is dated on this day. It starts at the invoice date
            {dialog?.minDate ? `, or ${formatDate(dialog.minDate)} if finished goods were transferred or a cost absorption month was posted after it` : ''}.
            Pick another date to override it; it cannot be earlier than {dialog?.minDate ? formatDate(dialog.minDate) : 'the last costing event of the order'} or later than today.
          </Alert>
          <TextField data-help="costing.orders.dlg.date" label="COGS date" type="date" size="small" value={costDate} InputLabelProps={{ shrink: true }}
            onChange={e => setCostDate(e.target.value)}
            inputProps={{ min: dialog?.minDate ?? undefined, max: dialog?.maxDate ?? toLocalIsoDate() }} />
        </DialogContent>
        <DialogActions>
          <Button data-help="costing.orders.dlg.cancel" onClick={() => setDialog(null)}>Cancel</Button>
          <Button data-help="costing.orders.dlg.confirm" variant="contained" disabled={!costDate || busy !== null} onClick={() => dialog && costShipment(dialog, costDate)}>Cost shipment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderCosts;
